import React, { useState } from 'react';
import {
    Box, Typography, Button, TextField, Select, MenuItem,
    Switch, IconButton, Dialog,
    DialogTitle, DialogContent, DialogActions, CircularProgress,
    useMediaQuery, useTheme
} from '@mui/material';
import {
    Close as CloseIcon,
    CalendarToday as CalendarIcon,
    LocalOffer as TagIcon,
    KeyboardArrowDown as ArrowDownIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from "../../services/api";



const dialogPaperSx = {
    background: 'linear-gradient(135deg, #1a1510 0%, #0f0d0a 100%)',
    border: '1px solid rgba(188, 150, 94, 0.89)',
    borderRadius: 1,
    boxShadow: '0 25px 60px rgba(0, 0, 0, 0.8)',
};

// Fieldset-style wrapper: label sits above the box, gap cut into the top border,
// exactly like the reference screenshot. Reuses the same gold/dark palette.
const FieldGroup = ({ label, required, error, helperText, children, sx }) => (
    <Box sx={{ ...sx }}>
        <Box
            component="fieldset"
            sx={{
                border: '1px solid',
                borderColor: error ? '#f44336' : 'rgba(188, 150, 94, 0.92)',
                borderRadius: 1,
                m: 0,
                px: 1.5,
                pt: 0.5,
                pb: 1,
                minWidth: 0,
                background: 'rgba(255,255,255,0.03)',
                transition: 'border-color 0.15s ease',
                '&:hover': { borderColor: error ? '#f44336' : 'rgba(188,150,94,0.5)' },
                '&:focus-within': { borderColor: error ? '#f44336' : '#bc965e' },
            }}
        >
            <Box
                component="legend"
                sx={{
                    px: 0.75,
                    fontSize: 12,
                    lineHeight: 1,
                    color: error ? '#f44336' : 'rgba(255,255,255,0.6)',
                    letterSpacing: 0.3,
                }}
            >
                {label}{required && ' *'}
            </Box>
            {children}
        </Box>
        {helperText && (
            <Typography
                variant="caption"
                sx={{ display: 'block', mt: 0.5, ml: 1.5, color: error ? '#f44336' : 'rgba(255,255,255,0.4)' }}
            >
                {helperText}
            </Typography>
        )}
    </Box>
);

const bareInputSx = {
    '& .MuiInputBase-root': { '&:before, &:after': { display: 'none' } },
    '& .MuiInputBase-input': {
        color: 'white',
        fontSize: '0.95rem',
        py: 0.75,
        '&::placeholder': { color: 'rgba(255,255,255,0.4)', opacity: 1 },
    },
};

const bareSelectSx = {
    color: 'white',
    fontSize: '0.95rem',
    '&:before, &:after': { display: 'none' },
    '& .MuiSelect-select': { py: 0.75 },
    '& .MuiSvgIcon-root': { color: '#bc965e' },
};

const EMPTY_FORM = {
    CouponCode: '',
    DiscountType: '',
    DiscountValue: '',
    MinOrderAmount: '',
    MaxDiscountAmount: '',
    IsActive: true,
    ExpiryDate: '',
    ApplicableTo: '',
    ProductCode: '',
};

const AddCouponDialog = ({ open, onClose, onCreated }) => {
    const [form, setForm] = useState(EMPTY_FORM);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const resetAndClose = () => {
        setForm(EMPTY_FORM);
        setErrors({});
        onClose();
    };

    const updateField = (field, value) => {
        setForm(p => ({ ...p, [field]: value }));
        if (errors[field]) setErrors(p => ({ ...p, [field]: undefined }));
    };

    const validate = () => {
        const next = {};

        if (!form.CouponCode.trim()) {
            next.CouponCode = 'Coupon code is required';
        }

        if (!form.DiscountType) {
            next.DiscountType = 'Select a discount type';
        }

        if (!form.DiscountValue || isNaN(form.DiscountValue) || Number(form.DiscountValue) <= 0) {
            next.DiscountValue = 'Enter a valid discount value';
        } else if (form.DiscountType === 'percentage' && Number(form.DiscountValue) > 100) {
            next.DiscountValue = 'Percentage discount cannot exceed 100%';
        }

        if (!form.ApplicableTo) {
            next.ApplicableTo = 'Select where this coupon applies';
        } else if (form.ApplicableTo === 'product' && !form.ProductCode.trim()) {
            next.ProductCode = 'Product code is required';
        }

        if (!form.ExpiryDate) {
            next.ExpiryDate = 'Expiry date is required';
        } else {
            const today = new Date().toISOString().split('T')[0];
            if (form.ExpiryDate < today) {
                next.ExpiryDate = 'Expiry date cannot be in the past';
            }
        }

        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const handleSave = async () => {
        if (!validate()) return;

        setSaving(true);
        try {
            const payload = {
                CouponCode: form.CouponCode.toUpperCase().trim(),
                DiscountType: form.DiscountType,
                DiscountValue: Number(form.DiscountValue),
                MinOrderAmount: form.MinOrderAmount ? Number(form.MinOrderAmount) : 0,
                MaxDiscountAmount: form.MaxDiscountAmount ? Number(form.MaxDiscountAmount) : null,
                IsActive: form.IsActive,
                ExpiryDate: form.ExpiryDate || null,
                ApplicableTo: form.ApplicableTo,
                ProductCode: form.ApplicableTo === 'product' ? form.ProductCode : null,
            };

            const res = await api.post('/coupons', payload);

            if (res.data.success) {
                toast.success('Coupon created!');
                resetAndClose();
                onCreated();
            } else {
                toast.error(res.data.message || 'Failed to create coupon');
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to create coupon');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={() => !saving && resetAndClose()}
            maxWidth="sm"
            fullWidth
           
            PaperProps={{ sx: dialogPaperSx }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Create Coupon</Typography>
                <IconButton onClick={resetAndClose} disabled={saving} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ overflowY: 'auto' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>

                    <FieldGroup label="Coupon Code" required error={!!errors.CouponCode}
                        helperText={errors.CouponCode || 'Customers will enter this at checkout'}>
                        <TextField
                            value={form.CouponCode}
                            onChange={(e) => updateField('CouponCode', e.target.value.toUpperCase())}
                            placeholder="e.g. SAVE20"
                            fullWidth
                            variant="standard"
                            InputProps={{
                                disableUnderline: true,
                                endAdornment: <TagIcon sx={{ color: '#bc965e', fontSize: 18 }} />,
                            }}
                            inputProps={{ style: { fontFamily: 'monospace', fontWeight: 700, letterSpacing: 2 } }}
                            sx={bareInputSx}
                        />
                    </FieldGroup>

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                        <FieldGroup label="Type" required error={!!errors.DiscountType} sx={{ flex: 1, minWidth: 130 }}>
                            <Select
                                value={form.DiscountType}
                                onChange={(e) => updateField('DiscountType', e.target.value)}
                                variant="standard"
                                disableUnderline
                                displayEmpty
                                IconComponent={ArrowDownIcon}
                                fullWidth
                                sx={bareSelectSx}
                                MenuProps={{ PaperProps: { sx: { background: '#1a1510', border: '1px solid rgba(188,150,94,0.3)' } } }}
                            >
                                <MenuItem value="" disabled sx={{ color: 'rgba(255,255,255,0.4)' }}>Select type</MenuItem>
                                <MenuItem value="percentage" sx={{ color: 'white' }}>Percentage (%)</MenuItem>
                                <MenuItem value="fixed" sx={{ color: 'white' }}>Fixed Amount (₹)</MenuItem>
                            </Select>
                        </FieldGroup>

                        <FieldGroup
                            label={form.DiscountType === 'fixed' ? 'Discount (₹)' : 'Discount (%)'}
                            required
                            error={!!errors.DiscountValue}
                            helperText={errors.DiscountValue}
                            sx={{ flex: 1, minWidth: 120 }}
                        >
                            <TextField
                                value={form.DiscountValue}
                                onChange={(e) => updateField('DiscountValue', e.target.value)}
                                type="number"
                                variant="standard"
                                InputProps={{ disableUnderline: true }}
                                inputProps={{ min: 0, max: form.DiscountType === 'percentage' ? 100 : undefined }}
                                fullWidth
                                sx={bareInputSx}
                            />
                        </FieldGroup>
                    </Box>

                    <FieldGroup label="Applicable To" required error={!!errors.ApplicableTo}>
                        <Select
                            value={form.ApplicableTo}
                            onChange={(e) => {
                                setForm(p => ({ ...p, ApplicableTo: e.target.value, ProductCode: '' }));
                                setErrors(p => ({ ...p, ApplicableTo: undefined, ProductCode: undefined }));
                            }}
                            variant="standard"
                            disableUnderline
                            displayEmpty
                            IconComponent={ArrowDownIcon}
                            fullWidth
                            sx={bareSelectSx}
                            MenuProps={{ PaperProps: { sx: { background: '#1a1510', border: '1px solid rgba(188,150,94,0.3)' } } }}
                        >
                            <MenuItem value="" disabled sx={{ color: 'rgba(255,255,255,0.4)' }}>Select scope</MenuItem>
                            <MenuItem value="all" sx={{ color: 'white' }}>All Products (Entire Order)</MenuItem>
                            <MenuItem value="product" sx={{ color: 'white' }}>Specific Product Only</MenuItem>
                        </Select>
                    </FieldGroup>

                    {form.ApplicableTo === 'product' && (
                        <FieldGroup label="Product Code" required error={!!errors.ProductCode}
                            helperText={errors.ProductCode || 'Coupon will only apply to this specific product in cart'}>
                            <TextField
                                value={form.ProductCode}
                                onChange={(e) => updateField('ProductCode', e.target.value.toUpperCase())}
                                placeholder="e.g. PROD001"
                                fullWidth
                                variant="standard"
                                InputProps={{ disableUnderline: true }}
                                sx={bareInputSx}
                            />
                        </FieldGroup>
                    )}

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                        <FieldGroup label="Min Order Amount (₹)" helperText="Leave 0 for no minimum" sx={{ flex: '1 1 140px' }}>
                            <TextField
                                value={form.MinOrderAmount}
                                onChange={(e) => updateField('MinOrderAmount', e.target.value)}
                                type="number"
                                placeholder="0"
                                variant="standard"
                                InputProps={{ disableUnderline: true }}
                                fullWidth
                                sx={bareInputSx}
                            />
                        </FieldGroup>
                        <FieldGroup label="Max Discount (₹)" helperText="Cap for % discounts" sx={{ flex: '1 1 140px' }}>
                            <TextField
                                value={form.MaxDiscountAmount}
                                onChange={(e) => updateField('MaxDiscountAmount', e.target.value)}
                                type="number"
                                placeholder="Optional"
                                variant="standard"
                                InputProps={{ disableUnderline: true }}
                                fullWidth
                                sx={bareInputSx}
                            />
                        </FieldGroup>
                    </Box>

                    <FieldGroup label="Expiry Date" required error={!!errors.ExpiryDate}
                        helperText={errors.ExpiryDate || 'Click anywhere on the field to pick a date'}>
                        <TextField
                            value={form.ExpiryDate}
                            onChange={(e) => updateField('ExpiryDate', e.target.value)}
                            type="date"
                            fullWidth
                            variant="standard"
                            inputProps={{ min: new Date().toISOString().split('T')[0] }}
                            InputProps={{
                                disableUnderline: true,
                                endAdornment: <CalendarIcon sx={{ color: '#bc965e', fontSize: 18, pointerEvents: 'none' }} />,
                            }}
                            sx={{
                                ...bareInputSx,
                                position: 'relative',
                                '& input[type="date"]::-webkit-calendar-picker-indicator': {
                                    opacity: 0,
                                    position: 'absolute',
                                    inset: 0,
                                    width: '100%',
                                    height: '100%',
                                    cursor: 'pointer',
                                    zIndex: 1,
                                },
                            }}
                        />
                    </FieldGroup>

                    <Box sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        p: 2, borderRadius: 1, border: '1px solid rgba(188,150,94,0.2)',
                        background: 'rgba(255,255,255,0.02)',
                    }}>
                        <Box>
                            <Typography variant="body1" sx={{ color: 'white', fontWeight: 500 }}>Active</Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                                Inactive coupons cannot be used at checkout
                            </Typography>
                        </Box>
                        <Switch
                            checked={form.IsActive}
                            onChange={(e) => updateField('IsActive', e.target.checked)}
                            sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': { color: '#bc965e' },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#bc965e' },
                            }}
                        />
                    </Box>

                    {form.CouponCode && form.DiscountValue && (
                        <Box sx={{
                            p: 2, borderRadius: 1,
                            background: 'linear-gradient(135deg, rgba(188,150,94,0.1), rgba(212,175,55,0.05))',
                            border: '1px dashed rgba(188,150,94,0.4)',
                        }}>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block', mb: 0.5 }}>
                                PREVIEW
                            </Typography>
                            <Typography sx={{ color: '#bc965e', fontFamily: 'monospace', fontWeight: 700, fontSize: '1.1rem' }}>
                                {form.CouponCode}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mt: 0.5 }}>
                                {form.DiscountType === 'percentage' ? `${form.DiscountValue}% off` : `₹${form.DiscountValue} off`}
                                {form.ApplicableTo === 'product' && form.ProductCode
                                    ? ` on product ${form.ProductCode}`
                                    : ' on entire order'}
                                {form.MinOrderAmount > 0 ? ` · min order ₹${form.MinOrderAmount}` : ''}
                                {form.MaxDiscountAmount ? ` · max ₹${form.MaxDiscountAmount}` : ''}
                            </Typography>
                        </Box>
                    )}

                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3, gap: 1, flexDirection: { xs: 'column-reverse', sm: 'row' } }}>
                <Button onClick={resetAndClose} disabled={saving} fullWidth={isMobile}
                    sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving}
                    fullWidth={isMobile}
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                    sx={{
                        background: 'linear-gradient(45deg, #bc965e, #d4af37)',
                        fontWeight: 600,
                        '&:hover': { background: 'linear-gradient(45deg, #a8834d, #c19b2a)' },
                        '&:disabled': { background: 'rgba(188,150,94,0.3)', color: 'rgba(255,255,255,0.5)' },
                    }}
                >
                    {saving ? 'Saving...' : 'Create Coupon'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddCouponDialog;